/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import _, { each } from 'underscore';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { PrimaryButton, DefaultButton } from '@bingads-webui-react/fabric-control';
import { getODataErrors } from '@bingads-webui-campaign/api-errors';
import {
  getCampaignTargetsCriterions,
  converODataObjectToDeviceState,
  globalErrorMsg,
  showWarning,
  bidStrategyOptionKeys,
  getCampaignData, updateDeviceByResponse, handleApiErrors, clearSubViewApiErrors, scrollToErrors, invalidateCache, mergeCampaignStateWithErrors,
  subscribeUrlOptions, subscribeSiteExclusion, subscribeIPExclusion, createDefaultHotelGroup, send, sendErrors,
} from '@bingads-webui-campaign/hotel-common-upsert';
import { Errors, modelToViewModelDayTimeTargets, locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import instrumentation from '@bingads-webui-campaign-legacy/instrumentation/index';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import {
  patchForLocationTargetGrid, shouldSelectBundleOption, getBundleCountriesAndBundleLocationCriterions, parseLocationTargets,
} from '@bingads-webui-campaign/hotel-common-targets';
import { getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { AdvancedSettings, CampaignBasic, CampaignTitle, defaultCampaignBasicData, CampaignTargets, fieldsToCheck } from './components';
import { convertCampaignStateToCampaignJSONSchemaData, validateCampaignState, generateDataToInvalidState, getAdGroupBid, getUnSupportedBidAdjustmentFeatures } from './util';

export const scenarios = {
  settings: 'settings',
  creation: 'creation',
};

export const HotelCampaignBase = class HotelCampaignBase extends React.Component {
  constructor(props) {
    super(props);
    this.isCreation = props.scenario === scenarios.creation;
    this.initializeTheInstrumentation();

    this.state = {
      campaign: {
        ...defaultCampaignBasicData,
      },
      errMsg: {
      },
      initTargets: {},
      isLoading: true,
      campaignApiErrors: [],
      targetsApiErrors: {},
      advancedSettinsApiErrors: {},
      isDataFetched: false,
    };
    this.subViews = {};
    this.defaultAdGroupId = undefined;
    this.defaultAdGroupCreated = props.scenario === scenarios.settings;
    this.defaultHotelGroupCreated = props.scenario === scenarios.settings;
    this.adScheduleView = undefined;
    this.adDistributionView = undefined;
    this.removeWarningInterval = undefined;
    this.getAdDistributionView = () => this.adDistributionView;
    this.getAdScheduleView = () => this.adScheduleView;
    this.setAdScheduleView = (view) => {
      this.adScheduleView = view;
      this.subViews.adScheduleView = view;
      this.adScheduleView.koModel.hasErrors.subscribe((x) => {
        if (x) {
          this.patchErrMsg('adSchedule', globalErrorMsg);
          return;
        }
        this.patchErrMsg('adSchedule', '');
      });
    };
    this.setAdDistributionView = (view) => { this.adDistributionView = view; };
    this.setSiteExclusionView = (view) => { this.siteExclusionView = view; subscribeSiteExclusion(this); };
    this.setIPExclusionView = (view) => { this.IPExclusionView = view; subscribeIPExclusion(this); };
    this.setUrlOptionsView = (view) => {
      this.urlOptionsView = view;
      this.subViews.urlOptionsView = view;
      subscribeUrlOptions(this, globalErrorMsg);
    };

    const campaignEDM = edmCampaign.types.resolve('Model.Campaign');
    this.campaignResource = new campaignEDM.ResourceIdentifier();
  }

  // eslint-disable-next-line react/sort-comp
  handleCreateOrUpdateSuccess = (response, resolve) => {
    const {
      scope,
      siteMap,
    } = this.props;
    if (this.isCreation) {
      const campaignId = this.getCreatedCampaignId(response);
      send(this.activity, 'campaign_creation_succeeded', { campaignId });
      this.createDefaultHotelGroupAfterCampaignAdGroupCreation(response, () => {
        send(this.activity, 'all_succeeded');
        this.durationActivity.stopSignal('completed');
        scope.save(getRoutePathById(siteMap, 'campaigns'));
      }).finally(() => resolve(true));
    } else {
      // we backfill advanced settings models here, so we have correct model if the saveExtensions call fails.
      send(this.activity, 'campaign_update_succeeded');
      this.updateResponseData({ entity: response }, resolve);

      if (this.isExclusionsUpdated()) {
        this.saveExtensions().then(() => {
          send(this.activity, 'all_succeeded');
          this.durationActivity.stopSignal('completed');
          scope.save(getRoutePathById(siteMap, 'adgroups'));
        });
      } else {
        send(this.activity, 'all_succeeded');
        this.durationActivity.stopSignal('completed');
        scope.save(getRoutePathById(siteMap, 'adgroups'));
      }
    }
  };

  getCreatedCampaignId = (response) => {
    let entity = response;
    if (response && response.entity) {
      // eslint-disable-next-line prefer-destructuring
      entity = response.entity;
    }
    return entity.Id;
  };

  getCreatedDefaultAdGroupId = (response) => {
    let entity = response;
    if (response && response.entity) {
      // eslint-disable-next-line prefer-destructuring
      entity = response.entity;
    }
    const adGroups = entity.AdGroups;
    if (adGroups && adGroups.length > 0) {
      const adGroup = adGroups[0];
      if (adGroup.Id) {
        return adGroup.Id;
      }
    }
    return undefined;
  };

  handleCreatedDefaultAdGroup = (response) => {
    this.defaultAdGroupId = this.getCreatedDefaultAdGroupId(response);
  };

  scrollAndShowWarning() {
    this.removeWarningInterval = showWarning('campaign', this.props.i18n, this.removeWarningInterval);
    scrollToErrors('campaign');
  }

  createDefaultHotelGroupAfterCampaignAdGroupCreation = (response, callback) => {
    if (!this.defaultHotelGroupCreated) {
      const campaignId = this.getCreatedCampaignId(response);
      const adGroupId = this.getCreatedDefaultAdGroupId(response);

      if (adGroupId && campaignId) {
        const {
          cid,
          aid,
        } = this.props.scope.values;
        let bid = 0.05;
        let type = 'FixedBid';
        const {
          bidStrategy,
        } = this.state.campaign;
        switch (bidStrategy.option) {
          case bidStrategyOptionKeys.ManualCpc:
            bid = bidStrategy.AdGroupBid;
            type = 'FixedBid';
            break;
          case bidStrategyOptionKeys.PercentCpc:
            bid = bidStrategy.AdGroupPercentBid;
            type = 'RateBid';
            break;
          case bidStrategyOptionKeys.Commission:
            bid = bidStrategy.AdGroupCommissionRate;
            if (bid == null || bid.length === 0) {
              bid = bidStrategy.Commission;
            }
            type = 'RateBid';
            break;
          default:
            bid = 0.05;
            type = 'FixedBid';
            break;
        }
        return createDefaultHotelGroup(cid, aid, campaignId, adGroupId, type, bid).then(() => {
          send(this.activity, 'hotel_group_creation_succeeded');
          this.defaultHotelGroupCreated = true;
          if (callback) {
            callback();
          }
        }).catch((res) => {
          // TODO: should not get here.
          send(this.activity, 'hotel_group_creation_failed');
          sendErrors(this.activity, 'hotel_group_creation_error', res);
          let errors;
          if (res.errors) {
            errors = getODataErrors(res.errors, this.props.i18n);
          }
          if (errors && errors.length) {
            this.setState(prevState => ({
              campaignApiErrors: prevState.campaignApiErrors.concat(errors),
            }));
            this.scrollAndShowWarning();
          }
        });
      }
    }
    return new Promise((resolve) => {
      if (callback) {
        callback();
      }
      resolve(true);
    });
  };

  handleCreateOrUpdateError = (response, resolve) => {
    if (this.isCreation) {
      send(this.activity, 'campaign_creation_failed');
      sendErrors(this.activity, 'campaign_creation_error', response);
    } else {
      send(this.activity, 'campaign_update_failed');
      sendErrors(this.activity, 'campaign_update_error', response);
    }
    const allApiErrors = handleApiErrors(response, this.props.i18n, this.subViews, 'campaign');

    this.setState(prevState => ({
      campaign: {
        ...mergeCampaignStateWithErrors(prevState.campaign, allApiErrors),
      },
      campaignApiErrors: allApiErrors.entityApiErrors.General || [],
      targetsApiErrors: allApiErrors.targetsApiErrors,
      advancedSettinsApiErrors: allApiErrors.advancedSettinsApiErrors,
    }));
    this.updateResponseData(response, resolve);
  };

  createOrUpdateCampaign = (data) => {
    const createOrUpdateCall = this.isCreation ? this.campaignResource.bulkCreate.bind(this.campaignResource) : this.campaignResource.bulkUpdate.bind(this.campaignResource);
    return new Promise(resolve => createOrUpdateCall(data)
      .then(response => this.handleCreateOrUpdateSuccess(response, resolve))
      .catch(response => this.handleCreateOrUpdateError(response, resolve)));
  };

  getBundleCountries() {
    const {
      cid,
      aid,
    } = this.props.scope.values;
    const {
      serverContext,
    } = this.props;
    const lcid = serverContext ? serverContext.CurrentUser.LocaleID : '1033';
    const market = serverContext ? serverContext.CurrentUser.Market : 'US';
    const language = 'All';

    return getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market);
  }

  renderCampaignTargets(cid, aid, campaignId) {
    const getCampaignDeviceCriterionsPromise = getCampaignTargetsCriterions({
      type: "isof(Criterion,'Model.DeviceCriterion')", cid, aid, campaignId,
    });

    return getCampaignDeviceCriterionsPromise.then((campaignTargets) => {
      const defalutDeviceSettings = defaultCampaignBasicData.device.parentLevelSettings;
      let deviceCriterion = [...defalutDeviceSettings];
      if (campaignTargets.length) {
        deviceCriterion = converODataObjectToDeviceState(campaignTargets);
      }
      this.setState(prev => ({ initTargets: { ...prev.initTargets, device: campaignTargets.length > 0 ? deviceCriterion : [] } }));
      this.patchCampaign('device', {
        parentLevelSettings: [...defalutDeviceSettings],
        currentLevelSettings: deviceCriterion,
        useParentLevel: campaignTargets.length === 0,
        reset: true,
      });
    });
  }

  syncLocationCriterions(cid, aid, campaignId) {
    invalidateCache('Model.CampaignCriterion');
    const getLocationCriterionsPromise = getCampaignTargetsCriterions({
      type: "isof(Criterion,'Model.LocationCriterion') or isof(Criterion,'Model.RadiusCriterion')",
      cid,
      aid,
      campaignId,
    });

    return getLocationCriterionsPromise.then((rawLocations) => {
      const {
        locations,
      } = parseLocationTargets(rawLocations, 'campaign', campaignId);
      this.setState(prevState => ({
        campaign: {
          ...prevState.campaign,
          location: {
            ...prevState.campaign.location,
            currentLevelSettings: {
              ...prevState.campaign.location.currentLevelSettings,
              initLocations: locations,
            },
          },
        },
      }));
    });
  }

  fetchLocationCriterions(cid, aid, campaignId, getBundleCountriesPromise) {
    const getLocationCriterionsPromise = getCampaignTargetsCriterions({
      type: "isof(Criterion,'Model.LocationCriterion') or isof(Criterion,'Model.RadiusCriterion') or isof(Criterion,'Model.LocationIntentCriterion')",
      cid,
      aid,
      campaignId,
    });

    return Promise.all([getBundleCountriesPromise, getLocationCriterionsPromise]).then(([{ bundleCountries, bundleLocationCriterions }, rawLocations]) => {
      const {
        locations,
        intentId,
        intentOption,
      } = parseLocationTargets(rawLocations, 'campaign', campaignId);
      let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
      targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;
      this.patchCampaign('location', {
        currentLevelSettings: {
          initLocations: locations,
          currentLocations: locations,
          targetOption,
          intentId,
          ...intentOption,
        },
        bidMode: true,
        useParentLevel: locations.length === 0,
      });
      this.patchCampaign('bundleCountries', bundleCountries);
      this.patchCampaign('bundleLocationCriterions', bundleLocationCriterions.map(x => patchForLocationTargetGrid(x)));
    });
  }

  fetchDayTimeCriterions(cid, aid, campaignId) {
    const getDayTimeCriterionsPromise = getCampaignTargetsCriterions({
      type: "isof(Criterion,'Model.DayTimeCriterion')",
      cid,
      aid,
      campaignId,
    });

    return getDayTimeCriterionsPromise.then((dayTimeRanges) => {
      this.patchCampaign('dayTimeRanges', modelToViewModelDayTimeTargets(dayTimeRanges));
    });
  }

  fetchCampaignData(cid, aid, campaignId) {
    const getCampaignDataPromise = getCampaignData(cid, aid, campaignId);
    return getCampaignDataPromise
      .then(campaignData => this.setState(prevState => ({ campaign: { ...prevState.campaign, ...campaignData, curBudgetAmount: campaignData.budget.amount } })));
  }

  componentDidMount() {
    this.perfMarker.willFetchData();

    const {
      scenario,
    } = this.props;

    const getBundleCountriesPromise = this.getBundleCountries();

    if (scenario === scenarios.creation) {
      getBundleCountriesPromise.then(({ bundleCountries, bundleLocationCriterions }) => {
        this.patchCampaign('bundleCountries', bundleCountries);
        this.patchCampaign('bundleLocationCriterions', bundleLocationCriterions.map(x => patchForLocationTargetGrid(x)));
      }).finally(() => {
        this.perfMarker.willRender();
        this.setState(() => ({ isLoading: false, isDataFetched: true }));
      });
    } else if (scenario === scenarios.settings) {
      const {
        cid,
        aid,
        campaignId,
      } = this.props.scope.values;

      Promise.all([
        this.renderCampaignTargets(cid, aid, campaignId),
        this.fetchCampaignData(cid, aid, campaignId),
        this.fetchLocationCriterions(cid, aid, campaignId, getBundleCountriesPromise),
        this.fetchDayTimeCriterions(cid, aid, campaignId),
      ]).finally(() => {
        this.perfMarker.willRender();
        this.setState(() => ({ isLoading: false, isDataFetched: true }));
      });
    }
  }

  componentDidUpdate() {
    if (this.state.isLoading === false && this.state.isDataFetched === true && this.perfMarker.isDone === false) {
      this.durationActivity.startSignal();
      this.perfMarker.done();
    }
  }

  componentWillUnmount() {
    this.activity.stop();
    if (this.state.campaign && this.state.campaign.Id) {
      invalidateCache('Model.Campaign', [this.state.campaign.Id]);
    }
    if (this.removeWarningInterval) {
      this.removeWarningInterval();
    }
    if (this.adScheduleView) {
      this.adScheduleView.undelegateEvents().off().remove();
    }
  }

  // eslint-disable-next-line react/sort-comp
  patchCampaign = (key, value) => {
    this.setState(prevState => ({
      campaign: {
        ...prevState.campaign,
        [key]: value,
      },
    }));
  };
  patchErrMsg = (key, value) => {
    this.setState(prevState => ({
      errMsg: {
        ...prevState.errMsg,
        [key]: value,
      },
    }));
  };
  patchTargetsApiErrMsg = (key, value) => {
    this.setState(prevState => ({
      targetsApiErrors: {
        ...prevState.targetsApiErrors,
        [key]: value,
      },
    }));
  };
  patchAdvancedSettinsApiErrors = (key, value) => {
    this.setState(prevState => ({
      advancedSettinsApiErrors: {
        ...prevState.advancedSettinsApiErrors,
        [key]: value,
      },
    }));
  };
  getCurrentCampaignId() {
    const {
      currentCampaign,
    } = this.props;
    return currentCampaign && currentCampaign.Id;
  }

  validate = () => {
    const {
      serverContext,
    } = this.props;
    let validationData = validateCampaignState(this.state.campaign, serverContext, this.isCreation, this.patchErrMsg);
    const unSupportedFeatures = getUnSupportedBidAdjustmentFeatures(this.state.campaign.bidStrategy.option);
    validationData = Object.assign(
      validationData,
      { urlOptions: this.urlOptionsView ? this.urlOptionsView.validate() : true },
      { adSchedule: this.adScheduleView && !unSupportedFeatures.includes('adSchedule') ? this.adScheduleView.validate() : true }
    );
    const isValid = fieldsToCheck.every(x => validationData[x] === true);
    if (isValid !== true) {
      this.setState(prevState => ({
        campaign: {
          ...generateDataToInvalidState(validationData, prevState.campaign),
        },
      }));
    }
    return isValid;
  };

  getAdScheduleValues = () => {
    const deletedDayTimeCb = (deletedDayTimeCriterions) => {
      const currentCampaignId = this.getCurrentCampaignId();

      each(deletedDayTimeCriterions, (criterion) => {
        criterion.Status = 'Deleted';
        criterion.Campaign = {
          Id: currentCampaignId,
        };
      });
    };

    return this.adScheduleView ? this.adScheduleView.getValues(deletedDayTimeCb) : [];
  };

  getUrlOptionsValues = () => {
    const urlOptions = this.urlOptionsView ? this.urlOptionsView.toObject() : {};

    if (_.isEmpty(urlOptions.TrackingUrlTemplate)) {
      urlOptions.TrackingUrlTemplate = null;
    }
    if (_.isEmpty(urlOptions.UrlCustomParameters)) {
      urlOptions.UrlCustomParameters = null;
    }

    return urlOptions;
  };

  getExclusionsValues = () => {
    const siteExclusionData = this.siteExclusionView ? this.siteExclusionView.getValues() : {};
    const IPExclusionData = this.IPExclusionView ? this.IPExclusionView.getValues() : {};
    const Exclusions = {
      ExcludedSites: siteExclusionData,
      ExcludedIPs: IPExclusionData,
    };
    return { Exclusions };
  };

  getDateSettings = () => {
    if (this.isCreation && this.adScheduleView) {
      return this.adScheduleView.getDateSettings();
    }
    return {};
  };

  getAdGroup = (values) => {
    const defaultAdGroupName = 'Ad group #1';
    const adgroup = {
      CampaignName: values.name.value,
      Name: defaultAdGroupName,
      Medium: 'Search',
      PricingModel: 'CPC',
      Status: 'Active',
      AdGroupType: 'HotelAds',
      ...this.getDateSettings(),
      ...getAdGroupBid(values),
      ...values.netWorkOptions,
    };

    if (this.adScheduleView && this.adScheduleView.getAdScheduleUseSearcherTimeZone) {
      adgroup.AdScheduleUseSearcherTimeZone = this.adScheduleView.getAdScheduleUseSearcherTimeZone();
    }

    if (values.Id) {
      adgroup.Campaign = {
        Id: values.Id,
      };
    }

    if (this.defaultAdGroupId) {
      adgroup.Id = this.defaultAdGroupId;
    } else {
      adgroup.HotelAdGroupType = _.compact([values.adFormat.HPA && 'HotelAd', values.adFormat.PPA && 'PropertyAd']);
    }

    return adgroup;
  };

  getValues = () => {
    const values = {
      ...this.state.campaign,
      adScheduleData: this.getAdScheduleValues(),
      urlOptions: this.getUrlOptionsValues(),
      netWorkOptions: this.adDistributionView ? this.adDistributionView.toObject() : {},
    };

    if (this.adScheduleView && this.adScheduleView.getAdScheduleUseSearcherTimeZone) {
      values.AdScheduleUseSearcherTimeZone = this.adScheduleView.getAdScheduleUseSearcherTimeZone();
    }

    if (!this.defaultAdGroupCreated) {
      values.AdGroups = [this.getAdGroup(values)];
    }

    return values;
  };

  initializeTheInstrumentation = () => {
    if (this.isCreation) {
      const scenario = instrumentation.hotelCampaignCreation.create();

      this.activity = scenario.hotelCampaignCreation.create();
      // duration activity, from user could edit & all successeed/completed
      this.durationActivity = scenario.duration.create();
      this.perfMarker = this.props.perfMarker.createChild('HotelCampaignBaseCreation');
    } else {
      const scenario = instrumentation.hotelCampaignSettings.create();

      this.activity = scenario.hotelCampaignSettings.create();
      this.durationActivity = scenario.duration.create();
      this.perfMarker = this.props.perfMarker.createChild('HotelCampaignBaseSettings');
    }
    // the component life time
    this.activity.start();
    send(this.activity, 'enter_page');

    this.perfMarker.willInit();
  };

  syncLocationCriterionsPromise = (payload) => {
    const {
      cid,
      aid,
      campaignId,
    } = this.props.scope.values;
    if (campaignId) {
      return this.syncLocationCriterions(cid, aid, campaignId);
    } else if (payload && payload.entity && payload.entity.Id) {
      return this.syncLocationCriterions(cid, aid, payload.entity.Id);
    } else if (payload && payload.Id) {
      return this.syncLocationCriterions(cid, aid, payload.Id);
    }
    return Promise.resolve(true);
  };

  updateResponseData = (payload, resolve) => {
    if (!payload) {
      resolve(true);
      return;
    }
    // Fill back the data to the view;
    if (payload.entity && payload.entity.Id) {
      this.patchCampaign('Id', payload.entity.Id);
    }
    const resourceName = 'CampaignCriterions';

    if (this.adScheduleView) {
      this.adScheduleView.backFillItems(payload.entity ? payload : { entity: payload });
    }

    this.handleCreatedDefaultAdGroup(payload);

    let resources = [];
    if (_.has(payload, 'entity') && _.has(payload.entity, resourceName)) {
      resources = payload.entity[resourceName];
    } else if (_.has(payload, resourceName)) {
      resources = payload[resourceName];
    }
    const DeviceCriterion = [];
    const ServerDeviceCriterion = [];
    _.each(resources, (criterion) => {
      switch (criterion.CriterionType) {
        case 'Device':
        case 'OriginalDevice':
          DeviceCriterion.push(criterion);
          if (!criterion.errors) {
            ServerDeviceCriterion.push(criterion);
          }
          break;
        default:
          break;
      }
    });
    const currentCriterionMap = {};
    if (DeviceCriterion.length > 0) {
      updateDeviceByResponse(DeviceCriterion, this.state.campaign.device, this.patchCampaign);
    }
    if (ServerDeviceCriterion.length > 0) {
      const currentDeviceCriterion = updateDeviceByResponse(ServerDeviceCriterion, this.state.campaign.device);
      currentCriterionMap.device = currentDeviceCriterion;
    }

    this.setState(prev => ({ initTargets: { ...prev.initTargets, ...currentCriterionMap } }));
    if (payload && payload.errors) {
      this.removeWarningInterval = showWarning('campaign', this.props.i18n, this.removeWarningInterval);
    }

    Promise.all([this.syncLocationCriterionsPromise(payload), this.createDefaultHotelGroupAfterCampaignAdGroupCreation(payload)])
      .finally(() => resolve(true));
  };

  isExclusionsUpdated() {
    if (!this.siteExclusionView && !this.IPExclusionView) {
      return false;
    }

    const siteExclusionsUpdated = this.siteExclusionView &&
      !_.isEqual(this.siteExclusionView.options.exclusions, this.siteExclusionView.getValues());

    const IPExclusionsUpdated = this.IPExclusionView &&
      !_.isEqual(this.IPExclusionView.options.exclusions, this.IPExclusionView.getValues());

    return siteExclusionsUpdated || IPExclusionsUpdated;
  }

  saveExtensions() {
    const { campaignId } = this.props.scope.values;
    const data = {
      campaignId,
      AdvanceSettings: {
        ...this.getExclusionsValues(),
      },
    };
    const that = this;
    return exclusionRepository.saveExtensions(data, 'campaign')
      .then(() => {
        send(this.activity, 'hotel_campaign_extensions_update_succeeded');
        that.IPExclusionView.clearApiErrors();
        that.siteExclusionView.clearApiErrors();
        that.patchErrMsg('baseExclusion', '');
        this.patchAdvancedSettinsApiErrors('baseExclusion', []);
      })
      .catch((response) => {
        send(this.activity, 'hotel_campaign_extensions_update_failed');
        if (response.NegativeSitesResult || response.NegativeIPsResult) {
          const { NegativeSitesResult: siteErrors, NegativeIPsResult: ipErrors } = response;
          const exclusionApiErrors = [];
          if (siteErrors || ipErrors) {
            if (that.siteExclusionView && siteErrors) {
              that.siteExclusionView.showErrors(siteErrors);
              exclusionApiErrors.push({ siteErrors });
            } else {
              that.siteExclusionView.clearApiErrors();
            }
            if (that.IPExclusionView && ipErrors) {
              that.IPExclusionView.showErrors(ipErrors);
              exclusionApiErrors.push({ ipErrors });
            } else {
              that.IPExclusionView.clearApiErrors();
            }
            that.patchErrMsg('baseExclusion', globalErrorMsg);
            this.patchAdvancedSettinsApiErrors('baseExclusion', exclusionApiErrors);
            scrollToErrors('campaign');
          } else {
            that.patchErrMsg('baseExclusion', '');
            this.patchAdvancedSettinsApiErrors('baseExclusion', []);
          }
          // eslint-disable-next-line no-throw-literal
          throw 'exclusion error';
        }
      });
  }

  clearAllApiErrors() {
    clearSubViewApiErrors(this.subViews);
    this.setState(prev => ({
      targetsApiErrors: {},
      advancedSettinsApiErrors: { baseExclusion: prev.advancedSettinsApiErrors.baseExclusion },
    }));
  }

  save = () => {
    send(this.activity, 'save_clicked');
    // If something is wrong, you want show error message in the titlebar, you can patchErrMsg like below.
    // this.patchErrMsg('campaign', '*Required field'); // The two parameters are the key of Collapse.Panel, and the error message displayed.
    const isValid = this.validate();
    if (isValid) {
      const data = this.getValues();
      this.setState(() => ({ isLoading: true }));
      const createOrUpdatePromise = this.createOrUpdateCampaign(convertCampaignStateToCampaignJSONSchemaData(data, this.state.initTargets));
      createOrUpdatePromise.finally(() => this.setState(() => ({ isLoading: false })));
    } else {
      scrollToErrors('campaign');
    }
  };

  cancel = () => {
    send(this.activity, 'cancel_clicked');
    this.durationActivity.stopSignal('canceled');
    const {
      scope,
      siteMap,
    } = this.props;
    scope.save(getRoutePathById(siteMap, 'campaigns'));
  };

  render() {
    const {
      classes,
      i18n,
      scenario,
    } = this.props;

    return (
      <div
        className={classnames(classes.hotelCampaignBase, `hotel-campaign-${scenario}-app`)}
        onFocus={() => { if (!_.isEmpty(this.state.campaignApiErrors)) { _.delay(() => this.setState(() => ({ campaignApiErrors: [] })), 200); } }}
      >
        <UiBlocker
          blocking={this.state.isLoading}
          loadingText={i18n.getString(_TL_('Loading...'))}
        >
          <div className={classnames(`hotel-campaign-${scenario}-container`, 'hotel-campaign-shared')}>
            {this.isCreation && <CampaignTitle permissions={this.props.permissions} />}
            <Errors errors={!_.isEmpty(this.state.campaignApiErrors) && this.state.campaignApiErrors.map(x => x.message)} />
            <CampaignBasic
              campaign={this.state.campaign}
              patchCampaign={this.patchCampaign}
              scenario={scenario}
              errMsg={this.state.errMsg}
              patchErrMsg={this.patchErrMsg}
              getAdScheduleView={this.getAdScheduleView}
              permissions={this.props.permissions}
              activity={this.activity}
            />
            <AdvancedSettings
              campaign={this.state.campaign}
              patchCampaign={this.patchCampaign}
              setAdScheduleView={this.setAdScheduleView}
              getAdScheduleView={this.getAdScheduleView}
              setAdDistributionView={this.setAdDistributionView}
              getAdDistributionView={this.getAdDistributionView}
              setBaseExclusionView={this.setBaseExclusionView}
              setUrlOptionsView={this.setUrlOptionsView}
              setSiteExclusionView={this.setSiteExclusionView}
              setIPExclusionView={this.setIPExclusionView}
              scenario={scenario}
              errMsg={this.state.errMsg}
              patchErrMsg={this.patchErrMsg}
              getUrlOptionsValues={this.getUrlOptionsValues}
              getExclusionsValues={this.getExclusionsValues}
              advancedSettinsApiErrors={this.state.advancedSettinsApiErrors}
              adScheduleModel={this.adScheduleModel}
              isDataFetched={this.state.isDataFetched}
            />
            <CampaignTargets
              campaign={this.state.campaign}
              patchCampaign={this.patchCampaign}
              errMsg={this.state.errMsg}
              patchErrMsg={this.patchErrMsg}
              scope={this.props.scope}
              targetsApiErrors={this.state.targetsApiErrors}
              patchTargetsApiErrMsg={this.patchTargetsApiErrMsg}
            />
            <div className="dashline" />
            <PrimaryButton className="save-campaign-btn" text={i18n.getString(_TL_('Save'))} onClick={this.save} />
            <DefaultButton className="cancel-btn" text={i18n.getString(_TL_('Cancel'))} onClick={this.cancel} />
          </div>
        </UiBlocker>
      </div>
    );
  }
};

HotelCampaignBase.propTypes = {
  currentCampaign: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  classes: PropTypes.shape({}).isRequired,
  scenario: PropTypes.string,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      campaignId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  siteMap: PropTypes.shape([]).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}),
};

HotelCampaignBase.defaultProps = {
  scenario: scenarios.creation,
  permissions: {},
};
